import React, { useState, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LabelList } from 'recharts';
import Loading from '../../components/Loading';
import NoDataAvailableMessage from '../../components/NoDataAvailable';
import { useSelector } from 'react-redux';
import { useTheme, useMediaQuery } from '@mui/material';
import { CUSTOM_COLORS } from '../components/gradientDefs';

const HourlyLineChart = ({ selectedMac, processedData, chartIsLoading, error, showLabels }) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

    const [activeLegends, setActiveLegends] = useState([]);
    const themeMode = useSelector(({ global }) => global.mode);

    // Function to shuffle the CUSTOM_COLORS array
    const shuffleColors = (array) => {
        for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [array[i], array[j]] = [array[j], array[i]];
        }
        return array;
    };

    // Process data and assign unique colors
    const { hourlyData, allLineNames, chartData, uniqueColors } = useMemo(() => {
        const data = processedData && processedData[selectedMac] ? processedData[selectedMac] : null;

        const lineNames = data ? Array.from(new Set(Object.values(data).flatMap((hourlyCounts) => Object.keys(hourlyCounts)))) : [];
        const sortedLineNames = lineNames.sort((a, b) => {
            const aNum = parseFloat(a);
            const bNum = parseFloat(b);
            if (!isNaN(aNum) && !isNaN(bNum)) {
                return aNum - bNum;
            }
            return a.localeCompare(b);
        });

        const chartData = Array.from({ length: 24 }, (_, hour) => {
            const hourData = { hour: `${hour}:00` };
            sortedLineNames.forEach((name) => (hourData[name] = 0));
            return hourData;
        });

        if (data) {
            Object.entries(data).forEach(([hour, counts]) => {
                const index = parseInt(hour);
                if (!isNaN(index) && index >= 0 && index < 24) {
                    Object.entries(counts).forEach(([logic, count]) => {
                        chartData[index][logic] = count;
                    });
                }
            });
        }

        const shuffledColors = shuffleColors([...CUSTOM_COLORS]);
        const uniqueColors = sortedLineNames.reduce((acc, lineName, index) => {
            acc[lineName] = shuffledColors[index % shuffledColors.length];
            return acc;
        }, {});

        return {
            hourlyData: data,
            allLineNames: sortedLineNames,
            chartData,
            uniqueColors,
        };
    }, [processedData, selectedMac]);

    // Error Handling
    if (chartIsLoading) {
        return <Loading />;
    }

    if (error || error?.status === 404 || !hourlyData || Object.keys(hourlyData).length === 0) {
        return <NoDataAvailableMessage />;
    }

    const textColor = themeMode === 'light' ? '#000000' : '#FFFFFF';

    // Handle legend click
    const handleLegendClick = (legendItem) => {
        setActiveLegends((prevActiveLegends) =>
            prevActiveLegends.includes(legendItem.dataKey) ? prevActiveLegends.filter((item) => item !== legendItem.dataKey) : [...prevActiveLegends, legendItem.dataKey]
        );
    };

    const hiddenLines = activeLegends.reduce((acc, key) => {
        acc[key] = true;
        return acc;
    }, {});

    return (
        <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData} margin={isSmallScreen ? { top: 30, right: 20, bottom: 20, left: 10 } : { top: 20, right: 30, bottom: 10, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                <XAxis
                    dataKey="hour"
                    angle={isSmallScreen ? -50 : 0}
                    textAnchor={isSmallScreen ? 'end' : 'middle'}
                    interval={isSmallScreen ? 3 : isTablet ? 2 : 0}
                    tick={{ fill: textColor, fontSize: isSmallScreen ? 10 : 12 }}
                />
                <YAxis tick={{ fill: textColor }} width={30} />
                <Tooltip
                    contentStyle={{
                        backgroundColor: theme.palette.background.default,
                        border: 'none',
                        borderRadius: '8px',
                        color: theme.palette.text.primary,
                    }}
                />
                <Legend
                    align="center"
                    onClick={handleLegendClick}
                    wrapperStyle={{
                        paddingTop: isSmallScreen ? 30 : 20,
                    }}
                    formatter={(value) => (
                        <span
                            style={{
                                color: hiddenLines[value] ? theme.palette.text.disabled : theme.palette.text.primary,
                                cursor: 'pointer',
                            }}
                        >
                            {value}
                        </span>
                    )}
                />
                {allLineNames.map((lineId) => (
                    <Line key={lineId} type="monotone" dataKey={lineId} strokeWidth={2} dot={true} name={lineId} hide={hiddenLines[lineId]} stroke={uniqueColors[lineId]}>
                        {showLabels && (
                            <LabelList dataKey={lineId} position="top" fill={textColor} fontSize={isSmallScreen ? 10 : 12} formatter={(value) => (value > 0 ? value.toLocaleString() : '')} />
                        )}
                    </Line>
                ))}
            </LineChart>
        </ResponsiveContainer>
    );
};

export default HourlyLineChart;
